import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, isRef as _isRef, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "mb-4" }
const _hoisted_2 = { class: "my-2" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 1 }

import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/store/user';
import { deepParseDates, formatDate } from '@/utils/date-utils';
import { useQuery } from '@vue/apollo-composable';
import { OrganizationsDocument, OrganizationType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import OrganizationForm from '@/components/organization/OrganizationForm.vue';
import { ORGANIZATION_USER_ROLES, PAGING_ITEMS_PER_PAGE } from '@/constants';

// user store to get user ID or token-based identity

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminOrganizationListView',
  setup(__props) {

const userStore = useUserStore();

// Filtering and paging state
const namePattern = ref('');
const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return organizationsQueryResult.value?.organizations?.total || 0;
});

// New organization form state
const showNewOrgForm = ref(false);
const showModalOrgDetail = ref(false);
const showModalOrgUpdate = ref(false);
const selectedOrg = ref<OrganizationType | null>(null);

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: false,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'ownerFullName',
    label: 'Organization owner',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'ownerEmail',
    label: 'Organization owner email',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'addressCountryCode',
    label: 'Country',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'salesforceId',
    label: 'Salesforce ID',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'id',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

// --- Query Organizations ---
const {
  result: organizationsQueryResult,
  loading: organizationsQueryInProgress,
  error: organizationsQueryError,
  refetch: refetchOrganizations,
} = useQuery(OrganizationsDocument, {
  namePattern: namePattern.value,
  page: pagingPageCurrent.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

const organizations = computed<OrganizationType[]>(() => {
  return (
    organizationsQueryResult.value?.organizations?.organizations
      ?.filter((org) => org && org !== null)
      .map((org) => ({ ...(deepParseDates(org) as OrganizationType) })) || []
  );
});

function organizationOwnerEmail(org: OrganizationType) {
  const ownerAssoc = org?.usersAssociated?.find(
    (assoc) =>
      assoc?.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc?.removedAt == null
  );
  return ownerAssoc?.user?.email ?? '';
}

function organizationOwnerFullName(org: OrganizationType) {
  const ownerAssoc = org?.usersAssociated?.find(
    (assoc) =>
      assoc?.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc?.removedAt == null
  );
  return `${ownerAssoc?.user?.givenName || ''} ${
    ownerAssoc?.user?.familyName || ''
  }`.trim();
}

function clearFilter() {
  namePattern.value = '';
  pagingPageCurrent.value = 1;

  requestRefetchOrganizations();
}

// --- Handlers ---
function orgDetailRequested(org: OrganizationType) {
  console.debug('Organization detail requested:', org.id);
  selectedOrg.value = org;
  showModalOrgDetail.value = true;
}

function orgUpdateRequested(org: OrganizationType) {
  console.debug('Organization update requested:', org.id);
  selectedOrg.value = org;
  showModalOrgUpdate.value = true;
}

function onOrganizationCreated() {
  showNewOrgForm.value = false;
  requestRefetchOrganizations();
}

function onOrganizationUpdated() {
  closeModals();
  requestRefetchOrganizations();
}

function closeModals() {
  selectedOrg.value = null;
  showModalOrgDetail.value = false;
  showModalOrgUpdate.value = false;
}

// Functions to refetch data
function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchOrganizations();
  }
}

function requestRefetchOrganizations() {
  refetchOrganizations({
    namePattern: namePattern.value,
    page: pagingPageCurrent.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.then((res) => {
      if (res.data && res.data.organizations) {
        console.debug('Refetch successful:', res.data.organizations);
      } else {
        console.error('Refetch failed, missing data:', res.data);
      }
    })
    .catch((err) => {
      console.error('Refetch failed:', err);
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchOrganizations();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'building'] }),
      _cache[7] || (_cache[7] = _createTextVNode(" Organizations", -1))
    ]),
    _cache[17] || (_cache[17] = _createElementVNode("h5", null, "Browse organizations", -1)),
    _cache[18] || (_cache[18] = _createElementVNode("hr", null, null, -1)),
    _cache[19] || (_cache[19] = _createElementVNode("p", null, " Here you can browse the list of organizations and their owners. You can also create a new organization. ", -1)),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(BButton), {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showNewOrgForm.value = !showNewOrgForm.value)),
          variant: "outline-dark",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), {
              icon: ['fas', 'plus'],
              class: "me-2"
            }),
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "me-2" }, "New organization", -1)),
            (showNewOrgForm.value)
              ? (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                  key: 0,
                  icon: ['fas', 'angle-up']
                }))
              : (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                  key: 1,
                  icon: ['fas', 'angle-down']
                }))
          ]),
          _: 1
        }),
        _createVNode(_unref(BCollapse), {
          modelValue: showNewOrgForm.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showNewOrgForm).value = $event)),
          id: "new-upload"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BCard), null, {
              default: _withCtx(() => [
                _createVNode(OrganizationForm, {
                  headerText: 'Create New Organization',
                  editable: true,
                  buttonText: 'Create New Organization',
                  showAdminFields: _unref(userStore).admin,
                  onOrganizationCreated: onOrganizationCreated
                }, null, 8, ["showAdminFields"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _createElementVNode("h2", null, [
        _createVNode(_unref(FontAwesomeIcon), {
          icon: ['fas', 'building'],
          class: "me-2"
        }),
        _cache[9] || (_cache[9] = _createTextVNode(" List of Organizations ", -1))
      ]),
      _createVNode(_unref(BAlert), {
        modelValue: _unref(organizationsQueryError),
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(organizationsQueryError) ? (organizationsQueryError).value = $event : null)),
        variant: "danger",
        dismissible: "",
        class: "mt-3 mb-0"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(organizationsQueryError)) + " ", 1),
          _createVNode(_unref(BButton), {
            onClick: requestRefetchOrganizations,
            variant: "outline-dark",
            class: "btn-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'sync'] }),
              _cache[10] || (_cache[10] = _createTextVNode(" Retry", -1))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(BForm), {
          class: "d-flex flex-row align-items-end flex-wrap gap-2",
          onSubmit: _withModifiers(onSearchRequested, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormGroup), {
              label: "Organization name",
              "label-for": "org-name"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "org-name",
                  type: "text",
                  modelValue: namePattern.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((namePattern).value = $event)),
                  placeholder: "e.g. Sampling Human"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-secondary",
              onClick: clearFilter
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "trash",
                  class: "me-1"
                }),
                _cache[11] || (_cache[11] = _createTextVNode(" Clear filter ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              type: "submit",
              disabled: _unref(organizationsQueryInProgress)
            }, {
              default: _withCtx(() => [
                (_unref(organizationsQueryInProgress))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: ""
                      }),
                      _cache[12] || (_cache[12] = _createTextVNode(" Searching ", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_4, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: "magnifying-glass",
                        class: "me-1"
                      }),
                      _cache[13] || (_cache[13] = _createTextVNode(" Refresh ", -1))
                    ]))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      (pagingTotalRows.value > pagingItemsPerPage.value)
        ? (_openBlock(), _createBlock(_unref(BPagination), {
            key: 0,
            modelValue: pagingPageCurrent.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((pagingPageCurrent).value = $event)),
            "total-rows": pagingTotalRows.value,
            "per-page": pagingItemsPerPage.value,
            pills: "",
            size: "sm"
          }, null, 8, ["modelValue", "total-rows", "per-page"]))
        : _createCommentVNode("", true),
      (!organizations.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[14] || (_cache[14] = [
            _createElementVNode("h5", { class: "my-5 py-5 text-center" }, "There are no organizations.", -1)
          ]))]))
        : (_openBlock(), _createBlock(_unref(BCard), { key: 2 }, {
            default: _withCtx(() => [
              _createVNode(_unref(BTable), {
                small: "",
                striped: "",
                responsive: "",
                hover: "",
                items: organizations.value,
                fields: fields,
                busy: _unref(organizationsQueryInProgress),
                class: "browser-table",
                onRowClicked: orgDetailRequested
              }, {
                "cell(name)": _withCtx((data) => [
                  _createElementVNode("strong", null, _toDisplayString(data.value), 1)
                ]),
                "cell(ownerFullName)": _withCtx((data) => [
                  _createTextVNode(_toDisplayString(organizationOwnerFullName(data.item)), 1)
                ]),
                "cell(ownerEmail)": _withCtx((data) => [
                  _createTextVNode(_toDisplayString(organizationOwnerEmail(data.item)), 1)
                ]),
                "cell(id)": _withCtx((data) => [
                  _createVNode(_unref(BButtonGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(BButton), {
                        variant: "outline-secondary",
                        class: "btn-sm",
                        onClick: ($event: any) => (orgDetailRequested(data.item))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'magnifying-glass'] })
                        ]),
                        _: 1
                      }, 8, ["onClick"]),
                      _createVNode(_unref(BButton), {
                        variant: "outline-secondary",
                        class: "btn-sm",
                        onClick: ($event: any) => (orgUpdateRequested(data.item))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'pencil'] })
                        ]),
                        _: 1
                      }, 8, ["onClick"])
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 1
              }, 8, ["items", "busy"])
            ]),
            _: 1
          }))
    ]),
    (selectedOrg.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 0,
          modelValue: showModalOrgDetail.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showModalOrgDetail).value = $event)),
          onClose: closeModals,
          title: "Organization detail"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              onClick: closeModals
            }, {
              default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                _createTextVNode("Close", -1)
              ]))]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(OrganizationForm, {
              organization: selectedOrg.value,
              headerText: '',
              editable: false,
              showAdminFields: _unref(userStore).admin
            }, null, 8, ["organization", "showAdminFields"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (selectedOrg.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 1,
          modelValue: showModalOrgUpdate.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showModalOrgUpdate).value = $event)),
          onClose: closeModals,
          title: "Update organization"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              onClick: closeModals
            }, {
              default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                _createTextVNode("Close", -1)
              ]))]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(OrganizationForm, {
              organization: selectedOrg.value,
              headerText: '',
              editable: true,
              buttonText: 'Update organization',
              showAdminFields: _unref(userStore).admin,
              onOrganizationUpdated: onOrganizationUpdated
            }, null, 8, ["organization", "showAdminFields"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true)
  ]))
}
}

})